/**
 * Recursive-descent parser for FlowScript.
 *
 * Two properties matter more than anything else here:
 *
 *  1. **It never throws.** The editor re-parses on every keystroke, and most
 *     keystrokes land on text that is not yet valid. A thrown exception would
 *     blank the diagram; instead every failure produces a diagnostic and an
 *     `ErrorStatement` placeholder, and parsing continues.
 *
 *  2. **Labels are source slices.** Free-form text is never reassembled from
 *     tokens — it is cut straight out of the original string, so capitalisation,
 *     punctuation, accents and emoji all survive untouched. Only whitespace runs
 *     are collapsed, which is what you want for a one-line node label.
 *
 * Statement termination is deliberately forgiving: a statement ends at `;`, but
 * a `}` or end-of-file also closes it silently (this is what makes the canonical
 * `Hello User!` example legal without a trailing semicolon), and a token that
 * starts a new line closes it with a "missing semicolon" warning rather than
 * swallowing the next line into the label.
 */
import type {
  Annotation,
  Block,
  BranchLabels,
  CallArgument,
  FunctionDeclaration,
  IfStatement,
  Label,
  NodeMetadata,
  Parameter,
  Program,
  Statement,
  SwitchCase,
  TopLevelStatement,
} from '../ast/nodes';
import { StableIdFactory } from '../ast/ids';
import type { SourcePosition, SourceRange } from '../ast/source';
import type { Diagnostic } from '../diagnostics/types';
import { DiagnosticCode, dedupeDiagnostics, sortDiagnostics } from '../diagnostics/types';
import type { Token } from '../tokenizer/tokens';
import { NO_KEYWORDS, STATEMENT_KEYWORDS, YES_KEYWORDS, describeToken } from '../tokenizer/tokens';
import { tokenize, withoutComments } from '../tokenizer/tokenizer';
import type { TokenizeLimits } from '../tokenizer/tokenizer';
import { resolveMetadata } from './metadata';

export interface ParseOptions {
  /** Guards against pathological nesting; the parser bails out past this. */
  readonly maxDepth: number;
  readonly tokenizeLimits?: TokenizeLimits;
}

export const DEFAULT_PARSE_OPTIONS: ParseOptions = {
  maxDepth: 64,
};

export interface ParseResult {
  readonly program: Program;
  readonly diagnostics: readonly Diagnostic[];
  /** Every token including comments — the formatter works from this. */
  readonly tokens: readonly Token[];
  readonly source: string;
}

const EMPTY_BRANCH_LABELS: BranchLabels = { yes: null, no: null };

export function parse(source: string, options: Partial<ParseOptions> = {}): ParseResult {
  const opts = { ...DEFAULT_PARSE_OPTIONS, ...options };
  const lexed = tokenize(source, opts.tokenizeLimits);
  const parser = new Parser(source, withoutComments(lexed.tokens), opts);
  const program = parser.parseProgram();
  const diagnostics = sortDiagnostics(
    dedupeDiagnostics([...lexed.diagnostics, ...parser.diagnostics]),
  );
  return { program, diagnostics, tokens: lexed.tokens, source };
}

class Parser {
  readonly diagnostics: Diagnostic[] = [];
  private cursor = 0;
  private depth = 0;
  private readonly ids = new StableIdFactory();

  constructor(
    private readonly source: string,
    private readonly tokens: readonly Token[],
    private readonly options: ParseOptions,
  ) {}

  /* ---------------------------------------------------------------- *
   * Token helpers
   * ---------------------------------------------------------------- */

  private cur(): Token {
    return this.tokens[Math.min(this.cursor, this.tokens.length - 1)]!;
  }

  private peek(ahead = 0): Token {
    return this.tokens[Math.min(this.cursor + ahead, this.tokens.length - 1)]!;
  }

  private get atEnd(): boolean {
    return this.cur().type === 'eof';
  }

  private advance(): Token {
    const token = this.cur();
    if (!this.atEnd) this.cursor += 1;
    return token;
  }

  private match(type: Token['type']): boolean {
    if (this.cur().type !== type) return false;
    this.advance();
    return true;
  }

  private checkWord(normalized: string, ahead = 0): boolean {
    const token = this.peek(ahead);
    return token.type === 'word' && token.normalized === normalized;
  }

  private get here(): SourcePosition {
    return this.cur().range.start;
  }

  private spanFrom(start: SourcePosition): SourceRange {
    const end = this.cursor > 0 ? this.tokens[this.cursor - 1]!.range.end : start;
    return { start, end: end.offset >= start.offset ? end : start };
  }

  private report(
    code: Diagnostic['code'],
    severity: Diagnostic['severity'],
    message: string,
    range: SourceRange,
    suggestion?: string,
    related?: Diagnostic['related'],
  ): void {
    // A runaway recovery loop would otherwise flood the problems panel.
    if (this.diagnostics.length > 500) return;
    this.diagnostics.push({ code, severity, message, range, suggestion, related });
  }

  /* ---------------------------------------------------------------- *
   * Labels
   * ---------------------------------------------------------------- */

  /** Cuts `[from, to)` out of the source and collapses whitespace runs. */
  private sliceLabel(from: number, to: number): string {
    return this.source.slice(from, Math.max(from, to)).replace(/\s+/g, ' ').trim();
  }

  private labelFrom(from: SourcePosition, to: SourcePosition): Label {
    return { text: this.sliceLabel(from.offset, to.offset), range: { start: from, end: to } };
  }

  /* ---------------------------------------------------------------- *
   * Program
   * ---------------------------------------------------------------- */

  parseProgram(): Program {
    const start = this.here;
    const body: TopLevelStatement[] = [];

    while (!this.atEnd) {
      const before = this.cursor;
      const item = this.parseTopLevel();
      if (item) body.push(item);
      // Absolute guarantee of forward progress: without this a malformed token
      // that no production consumes would hang the editor.
      if (this.cursor === before) this.advance();
    }

    return {
      kind: 'Program',
      body,
      range: { start, end: this.tokens[this.tokens.length - 1]!.range.end },
      stableId: this.ids.next('Program'),
    };
  }

  private parseTopLevel(): TopLevelStatement | null {
    const annotations = this.parseAnnotations();

    if (this.checkWord('function')) {
      return this.parseFunctionDeclaration(annotations);
    }

    // A stray `}` at top level: report and drop it, rather than treating the
    // rest of the document as unparseable.
    if (this.cur().type === 'rbrace') {
      const token = this.advance();
      this.report(
        DiagnosticCode.UnexpectedClosingBrace,
        'error',
        'There is no open block for this `}`.',
        token.range,
        'Remove it, or add the matching `{` above.',
      );
      return null;
    }

    return this.parseStatement(annotations);
  }

  /* ---------------------------------------------------------------- *
   * Annotations
   * ---------------------------------------------------------------- */

  private parseAnnotations(): Annotation[] {
    const annotations: Annotation[] = [];
    while (this.cur().type === 'at') {
      const start = this.here;
      this.advance(); // '@'

      const nameToken = this.cur();
      if (nameToken.type !== 'word') {
        this.report(
          DiagnosticCode.UnknownAnnotation,
          'warning',
          'Expected an annotation name after `@`.',
          { start, end: nameToken.range.end },
          'For example `@color("#2563eb")`.',
        );
        continue;
      }
      this.advance();

      const args: string[] = [];
      if (this.cur().type === 'lparen') {
        this.advance();
        while (!this.atEnd && this.cur().type !== 'rparen') {
          if (this.cur().type === 'comma') {
            this.advance();
            continue;
          }
          const argStart = this.here;
          if (this.cur().type === 'string') {
            args.push(this.advance().normalized);
            continue;
          }
          // Unquoted argument: read up to the next comma or `)`.
          while (
            !this.atEnd &&
            this.cur().type !== 'comma' &&
            this.cur().type !== 'rparen' &&
            this.cur().type !== 'lbrace' &&
            this.cur().type !== 'semicolon'
          ) {
            this.advance();
          }
          const argEnd = this.tokens[this.cursor - 1]!.range.end;
          args.push(this.sliceLabel(argStart.offset, argEnd.offset));
        }
        if (!this.match('rparen')) {
          this.report(
            DiagnosticCode.UnclosedParen,
            'error',
            'This annotation is missing its closing `)`.',
            this.spanFrom(start),
            'Add `)` to close the annotation.',
          );
        }
      }

      annotations.push({
        kind: 'Annotation',
        name: nameToken.normalized,
        rawName: nameToken.value,
        args,
        range: this.spanFrom(start),
      });
    }
    return annotations;
  }

  /** Validates annotations and folds their diagnostics into the parse result. */
  private metadataOf(annotations: readonly Annotation[]): NodeMetadata {
    const { metadata, diagnostics } = resolveMetadata(annotations);
    this.diagnostics.push(...diagnostics);
    return metadata;
  }

  /* ---------------------------------------------------------------- *
   * Declarations
   * ---------------------------------------------------------------- */

  private parseFunctionDeclaration(annotations: Annotation[]): FunctionDeclaration | null {
    const start = this.here;
    this.advance(); // 'function'

    const nameToken = this.cur();
    if (nameToken.type !== 'word' && nameToken.type !== 'string') {
      this.report(
        DiagnosticCode.ExpectedFunctionName,
        'error',
        `Expected a function name after \`function\`, found ${describeToken(nameToken)}.`,
        nameToken.range,
        'For example `function greet { … }`.',
      );
      this.synchronize();
      return null;
    }
    this.advance();
    const name: Label = {
      text: nameToken.type === 'string' ? nameToken.normalized : nameToken.value,
      range: nameToken.range,
    };

    const parameters = this.parseParameterList();
    const body = this.parseBlock();

    return {
      kind: 'FunctionDeclaration',
      name,
      normalizedName: name.text.toLowerCase(),
      parameters,
      body,
      range: this.spanFrom(start),
      stableId: this.ids.next('FunctionDeclaration', name.text.toLowerCase()),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private parseParameterList(): Parameter[] | null {
    if (this.cur().type !== 'lparen') return null;
    const open = this.advance();
    const parameters: Parameter[] = [];

    while (!this.atEnd && this.cur().type !== 'rparen' && this.cur().type !== 'lbrace') {
      if (this.cur().type === 'comma') {
        this.advance();
        continue;
      }
      const argStart = this.here;
      while (
        !this.atEnd &&
        this.cur().type !== 'comma' &&
        this.cur().type !== 'rparen' &&
        this.cur().type !== 'lbrace'
      ) {
        this.advance();
      }
      const argEnd = this.tokens[this.cursor - 1]!.range.end;
      const text = this.sliceLabel(argStart.offset, argEnd.offset);
      if (text) parameters.push({ name: text, range: { start: argStart, end: argEnd } });
    }

    if (!this.match('rparen')) {
      this.report(
        DiagnosticCode.UnclosedParen,
        'error',
        'This parameter list is missing its closing `)`.',
        { start: open.range.start, end: this.here },
        'Add `)` before the block.',
      );
    }
    return parameters;
  }

  /* ---------------------------------------------------------------- *
   * Blocks
   * ---------------------------------------------------------------- */

  private parseBlock(): Block {
    const start = this.here;

    if (this.cur().type !== 'lbrace') {
      this.report(
        DiagnosticCode.ExpectedBlock,
        'error',
        `Expected \`{\` to open a block, found ${describeToken(this.cur())}.`,
        this.cur().range,
        'Wrap the body in `{ … }`.',
      );
      return {
        kind: 'Block',
        statements: [],
        range: { start, end: start },
        stableId: this.ids.next('Block'),
      };
    }

    const open = this.advance();
    const statements: Statement[] = [];

    this.depth += 1;
    const tooDeep = this.depth > this.options.maxDepth;
    if (tooDeep) {
      this.report(
        DiagnosticCode.NestingTooDeep,
        'error',
        `Blocks are nested more than ${this.options.maxDepth} levels deep.`,
        open.range,
        'Extract part of the flow into a function.',
      );
    }

    while (!this.atEnd && this.cur().type !== 'rbrace') {
      if (tooDeep) {
        this.skipToMatchingBrace();
        break;
      }
      const before = this.cursor;
      const annotations = this.parseAnnotations();
      if (this.cur().type === 'rbrace' || this.atEnd) break;
      const statement = this.parseStatement(annotations);
      if (statement) statements.push(statement);
      if (this.cursor === before) this.advance();
    }
    this.depth -= 1;

    if (!this.match('rbrace')) {
      this.report(
        DiagnosticCode.UnclosedBrace,
        'error',
        'This block is never closed.',
        { start: open.range.start, end: open.range.end },
        'Add a `}` to close the block.',
        [{ message: 'Block opened here.', range: open.range }],
      );
    }

    return {
      kind: 'Block',
      statements,
      range: this.spanFrom(start),
      stableId: this.ids.next('Block'),
    };
  }

  /** Consumes tokens up to the `}` that balances the block already opened. */
  private skipToMatchingBrace(): void {
    let depth = 1;
    while (!this.atEnd) {
      if (this.cur().type === 'lbrace') depth += 1;
      else if (this.cur().type === 'rbrace') {
        depth -= 1;
        if (depth === 0) return;
      }
      this.advance();
    }
  }

  /* ---------------------------------------------------------------- *
   * Statements
   * ---------------------------------------------------------------- */

  private parseStatement(annotations: Annotation[] = []): Statement | null {
    if (this.atEnd) return null;

    // A leading backslash escapes keyword interpretation, so
    // `\Call the police;` is an ordinary action rather than a function call.
    if (this.cur().type === 'escape') {
      this.advance();
      return this.parseAction(annotations, this.here);
    }

    const token = this.cur();

    if (token.type === 'word' && STATEMENT_KEYWORDS.has(token.normalized)) {
      switch (token.normalized) {
        case 'function': {
          // A nested `function` is not legal; parse it anyway so the body is
          // not lost, and report it.
          const declaration = this.parseFunctionDeclaration(annotations);
          if (declaration) {
            this.report(
              DiagnosticCode.UnexpectedToken,
              'error',
              'Functions can only be declared at the top level.',
              declaration.range,
              'Move this declaration outside the surrounding block.',
            );
          }
          return null;
        }
        case 'if':
          return this.parseIf(annotations, false);
        case 'while':
          return this.parseWhile(annotations);
        case 'repeat':
          return this.parseRepeat(annotations);
        case 'for':
        case 'foreach':
          return this.parseForEach(annotations);
        case 'switch':
          return this.parseSwitch(annotations);
        case 'start':
          return this.parseTerminator(annotations, 'StartStatement');
        case 'end':
          return this.parseTerminator(annotations, 'EndStatement');
        case 'call':
          return this.parseCall(annotations);
        case 'return':
          return this.parseReturn(annotations);
        case 'break':
        case 'continue':
          return this.parseBreakOrContinue(annotations, token.normalized);
        case 'else':
          return this.parseStrayElse(annotations);
        case 'case':
        case 'default':
          return this.parseStrayCase(annotations, token.normalized);
        case 'until':
          return this.parseStrayUntil(annotations);
      }
    }

    return this.parseAction(annotations, this.here);
  }

  /* --- simple statements ------------------------------------------ */

  /**
   * Reads free text up to the statement terminator and returns the span. The
   * terminator rules live here and nowhere else.
   */
  private readStatementBody(): { end: SourcePosition; terminated: boolean } {
    const first = this.cursor;
    while (!this.atEnd) {
      const token = this.cur();
      if (token.type === 'semicolon' || token.type === 'rbrace' || token.type === 'lbrace') break;
      // A token on a fresh line ends the statement — otherwise a forgotten
      // semicolon would silently glue two lines into one label.
      if (this.cursor > first && token.precededByNewline) break;
      this.advance();
    }

    const end = this.cursor > first ? this.tokens[this.cursor - 1]!.range.end : this.here;

    if (this.cur().type === 'semicolon') {
      this.advance();
      return { end, terminated: true };
    }

    // `}` and end-of-file close a statement silently — this is what makes the
    // last statement of a block able to omit its semicolon.
    if (this.cur().type === 'rbrace' || this.atEnd) {
      return { end, terminated: true };
    }

    if (this.cur().type === 'lbrace') {
      this.report(
        DiagnosticCode.MissingSemicolon,
        'error',
        'Unexpected `{` — this statement is missing its `;`.',
        { start: end, end: this.here },
        'End the statement with `;`, or use `if`, `while`, `for each` or `switch` to open a block.',
      );
      return { end, terminated: false };
    }

    this.report(
      DiagnosticCode.MissingSemicolon,
      'warning',
      'This statement is missing its `;`.',
      { start: end, end },
      'Add `;` at the end of the line.',
    );
    return { end, terminated: false };
  }

  private parseAction(annotations: Annotation[], start: SourcePosition): Statement {
    const { end } = this.readStatementBody();
    const label = this.labelFrom(start, end);

    if (label.text === '') {
      // Nothing but punctuation — keep a placeholder so surrounding ids are stable.
      this.report(
        DiagnosticCode.EmptyLabel,
        'warning',
        'This statement has no text.',
        { start, end },
        'Write what happens here, or delete the line.',
      );
      return {
        kind: 'ErrorStatement',
        text: this.source.slice(start.offset, end.offset),
        range: { start, end },
        stableId: this.ids.next('ErrorStatement'),
        metadata: this.metadataOf(annotations),
        annotations,
      };
    }

    return {
      kind: 'ActionStatement',
      label,
      range: { start, end },
      stableId: this.ids.next('ActionStatement', label.text),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private parseTerminator(
    annotations: Annotation[],
    kind: 'StartStatement' | 'EndStatement',
  ): Statement {
    const start = this.here;
    const keywordToken = this.advance(); // 'start' | 'end'
    const labelStart = this.here;
    const { end } = this.readStatementBody();
    const raw = this.labelFrom(labelStart, end);
    const label = raw.text === '' ? null : raw;

    return {
      kind,
      label,
      keyword: keywordToken.value,
      range: { start, end },
      stableId: this.ids.next(kind, label?.text ?? ''),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private parseCall(annotations: Annotation[]): Statement {
    const start = this.here;
    this.advance(); // 'call'

    const nameToken = this.cur();
    if (nameToken.type !== 'word' && nameToken.type !== 'string') {
      this.report(
        DiagnosticCode.ExpectedCalleeName,
        'error',
        `Expected a function name after \`call\`, found ${describeToken(nameToken)}.`,
        nameToken.range,
        'For example `call greet;`.',
      );
      const { end } = this.readStatementBody();
      return {
        kind: 'ErrorStatement',
        text: this.source.slice(start.offset, end.offset),
        range: { start, end },
        stableId: this.ids.next('ErrorStatement'),
        metadata: this.metadataOf(annotations),
        annotations,
      };
    }
    this.advance();
    const callee: Label = {
      text: nameToken.type === 'string' ? nameToken.normalized : nameToken.value,
      range: nameToken.range,
    };

    let args: CallArgument[] | null = null;
    if (this.cur().type === 'lparen') {
      args = [];
      const open = this.advance();
      while (!this.atEnd && this.cur().type !== 'rparen' && this.cur().type !== 'semicolon') {
        if (this.cur().type === 'comma') {
          this.advance();
          continue;
        }
        const argStart = this.here;
        while (
          !this.atEnd &&
          this.cur().type !== 'comma' &&
          this.cur().type !== 'rparen' &&
          this.cur().type !== 'semicolon'
        ) {
          this.advance();
        }
        const argEnd = this.tokens[this.cursor - 1]!.range.end;
        const text = this.sliceLabel(argStart.offset, argEnd.offset);
        if (text) args.push({ text, range: { start: argStart, end: argEnd } });
      }
      if (!this.match('rparen')) {
        this.report(
          DiagnosticCode.UnclosedParen,
          'error',
          'This argument list is missing its closing `)`.',
          { start: open.range.start, end: this.here },
          'Add `)` before the `;`.',
        );
      }
    }

    const { end } = this.readStatementBody();
    return {
      kind: 'CallStatement',
      callee,
      args,
      range: { start, end },
      stableId: this.ids.next('CallStatement', callee.text.toLowerCase()),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private parseReturn(annotations: Annotation[]): Statement {
    const start = this.here;
    this.advance(); // 'return'
    const labelStart = this.here;
    const { end } = this.readStatementBody();
    const raw = this.labelFrom(labelStart, end);
    const label = raw.text === '' ? null : raw;

    return {
      kind: 'ReturnStatement',
      label,
      range: { start, end },
      stableId: this.ids.next('ReturnStatement', label?.text ?? ''),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private parseBreakOrContinue(annotations: Annotation[], word: 'break' | 'continue'): Statement {
    const start = this.here;
    this.advance();
    const trailingStart = this.here;
    const { end } = this.readStatementBody();
    const trailing = this.sliceLabel(trailingStart.offset, end.offset);
    if (trailing !== '') {
      this.report(
        DiagnosticCode.UnexpectedToken,
        'warning',
        `\`${word}\` does not take a label; ${JSON.stringify(trailing)} is ignored.`,
        { start: trailingStart, end },
        `Write just \`${word};\`.`,
      );
    }

    const kind = word === 'break' ? 'BreakStatement' : 'ContinueStatement';
    return {
      kind,
      range: { start, end },
      stableId: this.ids.next(kind),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  /* --- conditions -------------------------------------------------- */

  /**
   * Reads a parenthesised, paren-balanced condition. Returns the label plus
   * whether the parentheses were well formed.
   */
  private parseParenthesised(what: string): { label: Label; ok: boolean } {
    if (this.cur().type !== 'lparen') {
      const token = this.cur();
      this.report(
        DiagnosticCode.ExpectedCondition,
        'error',
        `Expected \`(\` after \`${what}\`, found ${describeToken(token)}.`,
        token.range,
        `Write \`${what} (…)\`.`,
      );
      return { label: { text: '', range: token.range }, ok: false };
    }

    const open = this.advance();
    const innerStart = this.here;
    let depth = 1;

    while (!this.atEnd) {
      const token = this.cur();
      if (token.type === 'lparen') depth += 1;
      else if (token.type === 'rparen') {
        depth -= 1;
        if (depth === 0) break;
      } else if (token.type === 'lbrace' || token.type === 'rbrace') {
        // A brace inside a condition means the `)` was forgotten. Stop before
        // consuming the block, so the rest of the document still parses.
        break;
      }
      this.advance();
    }

    const innerEnd = this.cursor > 0 ? this.tokens[this.cursor - 1]!.range.end : innerStart;
    const label = this.labelFrom(innerStart, innerEnd);

    if (!this.match('rparen')) {
      this.report(
        DiagnosticCode.UnclosedParen,
        'error',
        `This \`${what}\` condition is missing its closing \`)\`.`,
        { start: open.range.start, end: this.here },
        'Add `)` before the block.',
      );
      return { label, ok: false };
    }

    if (label.text === '') {
      this.report(
        DiagnosticCode.EmptyLabel,
        'warning',
        `This \`${what}\` has an empty condition.`,
        { start: open.range.start, end: this.tokens[this.cursor - 1]!.range.end },
        'Describe the condition, for example `(User is logged in?)`.',
      );
    }

    return { label, ok: true };
  }

  /**
   * Optional custom branch labels following a condition:
   * `if (Paid?) yes "Approved" no "Rejected"`.
   */
  private parseBranchLabels(): BranchLabels {
    let yes: Label | null = null;
    let no: Label | null = null;

    for (let i = 0; i < 2; i += 1) {
      const token = this.cur();
      if (token.type !== 'word') break;
      const isYes = YES_KEYWORDS.has(token.normalized);
      const isNo = NO_KEYWORDS.has(token.normalized);
      if (!isYes && !isNo) break;
      if ((isYes && yes) || (isNo && no)) break;

      const valueToken = this.peek(1);
      if (valueToken.type !== 'string') break; // not a branch-label clause after all

      this.advance(); // yes/no keyword
      this.advance(); // the string
      const label: Label = { text: valueToken.normalized, range: valueToken.range };
      if (isYes) yes = label;
      else no = label;
    }

    return { yes, no };
  }

  /* --- compound statements ----------------------------------------- */

  private parseIf(annotations: Annotation[], isElseIf: boolean): IfStatement {
    const start = this.here;
    this.advance(); // 'if'

    const { label: condition } = this.parseParenthesised('if');
    const branchLabels = this.parseBranchLabels();
    const consequent = this.parseBlock();

    let alternate: Block | IfStatement | null = null;
    if (this.checkWord('else')) {
      this.advance(); // 'else'
      if (this.checkWord('if')) {
        alternate = this.parseIf([], true);
      } else {
        alternate = this.parseBlock();
      }
    }

    return {
      kind: 'IfStatement',
      condition,
      branchLabels,
      consequent,
      alternate,
      isElseIf,
      range: this.spanFrom(start),
      stableId: this.ids.next('IfStatement', condition.text),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private parseWhile(annotations: Annotation[]): Statement {
    const start = this.here;
    this.advance();
    const { label: condition } = this.parseParenthesised('while');
    const branchLabels = this.parseBranchLabels();
    const body = this.parseBlock();

    return {
      kind: 'WhileStatement',
      condition,
      branchLabels,
      body,
      range: this.spanFrom(start),
      stableId: this.ids.next('WhileStatement', condition.text),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private parseRepeat(annotations: Annotation[]): Statement {
    const start = this.here;
    this.advance(); // 'repeat'
    const body = this.parseBlock();

    let condition: Label = { text: '', range: this.spanFrom(start) };
    let branchLabels: BranchLabels = EMPTY_BRANCH_LABELS;

    if (this.checkWord('until')) {
      this.advance();
      condition = this.parseParenthesised('until').label;
      branchLabels = this.parseBranchLabels();
      // The trailing `;` is conventional but optional.
      this.match('semicolon');
    } else {
      this.report(
        DiagnosticCode.ExpectedUntil,
        'error',
        `Expected \`until\` after the \`repeat\` block, found ${describeToken(this.cur())}.`,
        this.cur().range,
        'Write `repeat { … } until (Condition?);`.',
      );
    }

    return {
      kind: 'RepeatStatement',
      condition,
      branchLabels,
      body,
      range: this.spanFrom(start),
      stableId: this.ids.next('RepeatStatement', condition.text),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private parseForEach(annotations: Annotation[]): Statement {
    const start = this.here;
    const keyword = this.advance(); // 'for' | 'foreach'
    if (keyword.normalized === 'for' && this.checkWord('each')) {
      this.advance();
    }

    const { label: header } = this.parseParenthesised('for each');
    const branchLabels = this.parseBranchLabels();
    const body = this.parseBlock();

    // `Item in collection` is split for a nicer label, but the whole header is
    // kept so unusual phrasings still render sensibly.
    const split = splitForEachHeader(header);

    return {
      kind: 'ForEachStatement',
      header,
      item: split.item,
      collection: split.collection,
      branchLabels,
      body,
      range: this.spanFrom(start),
      stableId: this.ids.next('ForEachStatement', header.text),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private parseSwitch(annotations: Annotation[]): Statement {
    const start = this.here;
    this.advance(); // 'switch'
    const { label: discriminant } = this.parseParenthesised('switch');

    const cases: SwitchCase[] = [];

    if (this.cur().type !== 'lbrace') {
      this.report(
        DiagnosticCode.ExpectedBlock,
        'error',
        `Expected \`{\` after \`switch (…)\`, found ${describeToken(this.cur())}.`,
        this.cur().range,
        'Wrap the cases in `{ … }`.',
      );
    } else {
      const open = this.advance();
      this.depth += 1;

      while (!this.atEnd && this.cur().type !== 'rbrace') {
        const before = this.cursor;
        const caseAnnotations = this.parseAnnotations();
        if (this.cur().type === 'rbrace' || this.atEnd) break;

        const parsed = this.parseSwitchCase(caseAnnotations);
        if (parsed) cases.push(parsed);
        if (this.cursor === before) this.advance();
      }

      this.depth -= 1;
      if (!this.match('rbrace')) {
        this.report(
          DiagnosticCode.UnclosedBrace,
          'error',
          'This `switch` block is never closed.',
          open.range,
          'Add a `}` to close the switch.',
          [{ message: 'Switch opened here.', range: open.range }],
        );
      }
    }

    return {
      kind: 'SwitchStatement',
      discriminant,
      cases,
      range: this.spanFrom(start),
      stableId: this.ids.next('SwitchStatement', discriminant.text),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private parseSwitchCase(annotations: Annotation[]): SwitchCase | null {
    const start = this.here;
    const token = this.cur();

    let test: Label | null = null;

    if (token.type === 'word' && token.normalized === 'default') {
      this.advance();
    } else if (token.type === 'word' && token.normalized === 'case') {
      this.advance();
      const testStart = this.here;
      if (this.cur().type === 'string') {
        const value = this.advance();
        test = { text: value.normalized, range: value.range };
      } else {
        while (
          !this.atEnd &&
          this.cur().type !== 'colon' &&
          this.cur().type !== 'lbrace' &&
          this.cur().type !== 'rbrace'
        ) {
          this.advance();
        }
        const testEnd = this.tokens[this.cursor - 1]!.range.end;
        test = this.labelFrom(testStart, testEnd);
      }
      if (test.text === '') {
        this.report(
          DiagnosticCode.EmptyLabel,
          'warning',
          'This `case` has no value.',
          this.spanFrom(start),
          'Write the value, for example `case "Admin":`.',
        );
      }
    } else {
      this.report(
        DiagnosticCode.UnexpectedToken,
        'error',
        `Expected \`case\` or \`default\` inside \`switch\`, found ${describeToken(token)}.`,
        token.range,
        'Every branch of a switch starts with `case …:` or `default:`.',
      );
      this.skipToCaseBoundary();
      return null;
    }

    if (!this.match('colon')) {
      this.report(
        DiagnosticCode.MissingCaseColon,
        'error',
        'Expected `:` after this case label.',
        { start: this.here, end: this.here },
        'Write `case "Value":` or `default:`.',
      );
    }

    // The body may be a braced block or a bare run of statements up to the next
    // `case` / `default` / `}`.
    let body: Block;
    if (this.cur().type === 'lbrace') {
      body = this.parseBlock();
    } else {
      const bodyStart = this.here;
      const statements: Statement[] = [];
      this.depth += 1;
      while (
        !this.atEnd &&
        this.cur().type !== 'rbrace' &&
        !this.checkWord('case') &&
        !this.checkWord('default')
      ) {
        const before = this.cursor;
        const inner = this.parseAnnotations();
        if (this.cur().type === 'rbrace' || this.atEnd) break;
        if (this.checkWord('case') || this.checkWord('default')) break;
        const statement = this.parseStatement(inner);
        if (statement) statements.push(statement);
        if (this.cursor === before) this.advance();
      }
      this.depth -= 1;
      body = {
        kind: 'Block',
        statements,
        range: this.spanFrom(bodyStart),
        stableId: this.ids.next('Block'),
      };
    }

    return {
      kind: 'SwitchCase',
      test,
      body,
      range: this.spanFrom(start),
      stableId: this.ids.next('SwitchCase', test?.text ?? 'default'),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  private skipToCaseBoundary(): void {
    while (
      !this.atEnd &&
      this.cur().type !== 'rbrace' &&
      !this.checkWord('case') &&
      !this.checkWord('default')
    ) {
      this.advance();
    }
  }

  /* --- stray keyword recovery -------------------------------------- */

  private parseStrayElse(annotations: Annotation[]): Statement {
    const start = this.here;
    const token = this.advance(); // 'else'
    this.report(
      DiagnosticCode.ElseWithoutIf,
      'error',
      '`else` has no matching `if`.',
      token.range,
      'Add an `if (…) { … }` above, or delete this `else`.',
    );

    // Consume the else body so its contents do not cascade into more errors.
    if (this.checkWord('if')) {
      this.parseIf([], true);
    } else if (this.cur().type === 'lbrace') {
      this.parseBlock();
    }

    return this.errorStatement(annotations, start);
  }

  private parseStrayCase(annotations: Annotation[], word: 'case' | 'default'): Statement {
    const start = this.here;
    const token = this.cur();
    this.report(
      word === 'case' ? DiagnosticCode.CaseOutsideSwitch : DiagnosticCode.DefaultOutsideSwitch,
      'error',
      `\`${word}\` can only appear inside a \`switch\` block.`,
      token.range,
      'Wrap these branches in `switch (…) { … }`.',
    );
    this.skipToCaseBoundary();
    if (this.cur().type === 'lbrace') this.parseBlock();
    return this.errorStatement(annotations, start);
  }

  private parseStrayUntil(annotations: Annotation[]): Statement {
    const start = this.here;
    const token = this.advance();
    this.report(
      DiagnosticCode.UnexpectedToken,
      'error',
      '`until` can only follow a `repeat { … }` block.',
      token.range,
      'Write `repeat { … } until (Condition?);`.',
    );
    if (this.cur().type === 'lparen') this.parseParenthesised('until');
    this.match('semicolon');
    return this.errorStatement(annotations, start);
  }

  private errorStatement(annotations: Annotation[], start: SourcePosition): Statement {
    const range = this.spanFrom(start);
    return {
      kind: 'ErrorStatement',
      text: this.source.slice(start.offset, range.end.offset),
      range,
      stableId: this.ids.next('ErrorStatement'),
      metadata: this.metadataOf(annotations),
      annotations,
    };
  }

  /** Panic-mode recovery: skip to the next plausible statement boundary. */
  private synchronize(): void {
    while (!this.atEnd) {
      if (this.cur().type === 'semicolon') {
        this.advance();
        return;
      }
      if (this.cur().type === 'rbrace' || this.cur().type === 'lbrace') return;
      if (
        this.cur().type === 'word' &&
        this.cur().precededByNewline &&
        STATEMENT_KEYWORDS.has(this.cur().normalized)
      ) {
        return;
      }
      this.advance();
    }
  }
}

/** `Item in collection` -> `{ item, collection }`; anything else -> nulls. */
function splitForEachHeader(header: Label): { item: Label | null; collection: Label | null } {
  const match = /^(.*?)\s+in\s+(.*)$/i.exec(header.text);
  if (!match) return { item: null, collection: null };
  const [, itemText, collectionText] = match;
  if (!itemText || !collectionText) return { item: null, collection: null };
  return {
    item: { text: itemText.trim(), range: header.range },
    collection: { text: collectionText.trim(), range: header.range },
  };
}
